import { htmlToText } from '@frontend/utils/sanitizeHtml';

const IMAGE_SECTION_PREFIX = 'img__';
const TYPE_ID_MAX_LENGTH = 64;

let imageSectionSeq = 0;

function uniqueImageSectionTypeId(imageTypeKey = 'custom') {
  imageSectionSeq += 1;
  const unique = `${Date.now()}${imageSectionSeq}`;
  const key = String(imageTypeKey || 'custom').slice(
    0,
    Math.max(1, TYPE_ID_MAX_LENGTH - IMAGE_SECTION_PREFIX.length - 2 - unique.length),
  );
  return `${IMAGE_SECTION_PREFIX}${key}__${unique}`;
}

export function isImageSection(section, payload) {
  if (!section || !section.typeId) {
    return false;
  }
  const meta = getSectionMeta(section.typeId, payload);
  if (meta && meta.kind === 'image_type') {
    return true;
  }
  return section.typeId.startsWith(IMAGE_SECTION_PREFIX);
}

export function getSectionMeta(typeId, payload) {
  const store = payload && payload.section_meta;
  if (!store || typeof store !== 'object') {
    return null;
  }
  return store[typeId] || null;
}

export function setSectionMeta(typeId, meta, payload) {
  const next = { ...(payload || {}) };
  const store = { ...(next.section_meta || {}) };
  if (meta) {
    store[typeId] = meta;
  } else {
    delete store[typeId];
  }
  if (Object.keys(store).length) {
    next.section_meta = store;
  } else {
    delete next.section_meta;
  }
  return next;
}

export function createImageTypeSection(imageType) {
  const typeId = uniqueImageSectionTypeId(imageType && imageType.key);
  return {
    section: {
      typeId,
      label: imageType.name,
      content: '',
      visible: true,
    },
    meta: {
      kind: 'image_type',
      imageTypeKey: imageType.key,
      imageTypeName: imageType.name,
      category: imageType.category || '',
      width: imageType.width || null,
      height: imageType.height || null,
      quantity: 1,
      overlayText: '',
      conceptImageUrl: '',
      conceptImageSource: '',
    },
  };
}

export function createSectionsFromSequence(sequence, allImageTypes) {
  const slots = Array.isArray(sequence.slots)
    ? sequence.slots
    : String(sequence.slots || '')
      .split(' · ')
      .map((s) => s.trim())
      .filter(Boolean);

  const created = [];
  slots.forEach((slot) => {
    const imageType = resolveSlotToImageType(slot, allImageTypes);
    if (!imageType) {
      const label = typeof slot === 'string' ? slot : (slot.label || slot.name || 'Image');
      const typeId = uniqueImageSectionTypeId('custom');
      created.push({
        section: {
          typeId,
          label,
          content: '',
          visible: true,
        },
        meta: {
          kind: 'image_type',
          imageTypeKey: null,
          imageTypeName: label,
          category: '',
          width: null,
          height: null,
          quantity: 1,
          overlayText: '',
          conceptImageUrl: '',
      conceptImageSource: '',
        },
      });
      return;
    }
    created.push(createImageTypeSection(imageType));
  });

  return {
    sections: created.map((item) => item.section),
    metas: created.map((item) => ({ typeId: item.section.typeId, meta: item.meta })),
    sequenceMeta: {
      id: sequence.id,
      name: sequence.name || sequence.title || 'Sequence',
    },
  };
}

function resolveSlotToImageType(slot, allImageTypes) {
  const label = typeof slot === 'string'
    ? slot
    : (slot.label || slot.name || slot.key || '');
  const key = typeof slot === 'object' ? (slot.key || slot.type) : null;
  if (key) {
    const byKey = allImageTypes.find((t) => t.key === key);
    if (byKey) {
      return byKey;
    }
    const byAlias = allImageTypes.find((t) => Array.isArray(t.aliases) && t.aliases.includes(key));
    if (byAlias) {
      return byAlias;
    }
  }
  const normalized = String(label).trim().toLowerCase();
  return allImageTypes.find((t) => t.name.trim().toLowerCase() === normalized)
    || allImageTypes.find((t) => t.key === normalized.replace(/\s+/g, '_'));
}

export function applySectionMetaToSections(sections, payload) {
  if (!Array.isArray(sections)) {
    return;
  }
  const store = payload && payload.section_meta;
  if (!store || typeof store !== 'object') {
    sections.forEach((section) => {
      if (isOriginalMockupSection(section) && !section.imageMeta) {
        section.imageMeta = createOriginalMockupMeta();
      }
    });
    return;
  }
  sections.forEach((section) => {
    const meta = store[section.typeId];
    if (!meta) {
      if (isOriginalMockupSection(section) && !section.imageMeta) {
        section.imageMeta = createOriginalMockupMeta();
      }
      return;
    }
    if (meta.imageTypeName && !section.label) {
      section.label = meta.imageTypeName;
    }
    if (meta.kind === 'original_mockup') {
      section.imageMeta = ensureOriginalMockupMeta({ ...section, imageMeta: meta });
      if (!section.label) {
        section.label = originalMockupLabel(section.imageMeta.variant);
      }
      return;
    }
    section.imageMeta = { ...meta };
  });
}

export function syncSectionMetaFromSections(sections, payload) {
  const next = { ...(payload || {}) };
  const store = { ...(next.section_meta || {}) };
  (sections || []).forEach((section) => {
    if (!section.typeId || !section.imageMeta) {
      return;
    }
    store[section.typeId] = { ...section.imageMeta };
  });
  if (Object.keys(store).length) {
    next.section_meta = store;
  } else {
    delete next.section_meta;
  }
  return next;
}

export function imageSectionPlaceholder(section, payload) {
  const meta = section.imageMeta || getSectionMeta(section.typeId, payload);
  const name = (meta && meta.imageTypeName) || section.label || 'image';
  return `Creative direction for ${name}…`;
}

export const ORIGINAL_MOCKUP_TYPE_ID = 'original_mockup';
export const ORIGINAL_MOCKUP_VARIANTS = ['mockup', 'template'];

export function isOriginalMockupSection(section) {
  return Boolean(section && section.typeId === ORIGINAL_MOCKUP_TYPE_ID);
}

export function normalizeOriginalMockupVariant(value) {
  return ORIGINAL_MOCKUP_VARIANTS.includes(value) ? value : 'mockup';
}

export function originalMockupAfterLabel(variant) {
  return normalizeOriginalMockupVariant(variant) === 'template' ? 'Template' : 'Mockup';
}

export function originalMockupLabel(variant) {
  return normalizeOriginalMockupVariant(variant) === 'template'
    ? 'Original/Template'
    : 'Original/Mockup';
}

/** Prefer a shared pair variant for section title; mixed pairs use Original/Mockup. */
export function sectionVariantFromPairs(pairs, fallback = 'mockup') {
  const defaultVariant = normalizeOriginalMockupVariant(fallback);
  if (!Array.isArray(pairs) || !pairs.length) {
    return defaultVariant;
  }
  const variants = pairs.map((pair) => normalizeOriginalMockupVariant(
    (pair && pair.variant) || defaultVariant,
  ));
  const first = variants[0];
  return variants.every((value) => value === first) ? first : 'mockup';
}

export function createOriginalMockupPair(variant = 'mockup') {
  return {
    id: `pair_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    originalUrl: '',
    originalSource: '',
    afterUrl: '',
    afterSource: '',
    variant: normalizeOriginalMockupVariant(variant),
  };
}

export function createOriginalMockupMeta(variant = 'mockup') {
  const normalized = normalizeOriginalMockupVariant(variant);
  return {
    kind: 'original_mockup',
    variant: normalized,
    pairs: [createOriginalMockupPair(normalized)],
  };
}

export function ensureOriginalMockupMeta(section) {
  if (!isOriginalMockupSection(section)) {
    return null;
  }
  const existing = section.imageMeta && typeof section.imageMeta === 'object'
    ? section.imageMeta
    : null;
  if (existing && existing.kind === 'original_mockup' && Array.isArray(existing.pairs) && existing.pairs.length) {
    const fallbackVariant = normalizeOriginalMockupVariant(existing.variant);
    const pairs = existing.pairs.map((pair) => ({
      id: pair.id || createOriginalMockupPair().id,
      originalUrl: pair.originalUrl || '',
      originalSource: pair.originalSource || '',
      afterUrl: pair.afterUrl || '',
      afterSource: pair.afterSource || '',
      // Legacy metas only stored variant at section level — migrate onto each pair.
      variant: normalizeOriginalMockupVariant(pair.variant || fallbackVariant),
    }));
    return {
      kind: 'original_mockup',
      variant: sectionVariantFromPairs(pairs, fallbackVariant),
      pairs,
    };
  }
  return createOriginalMockupMeta(existing && existing.variant);
}

export function isCreativeDirectionSection(section) {
  const id = section && (section.typeId || section.type_id);
  return id === 'objective' || id === 'creative_direction';
}

/**
 * Derive the grid-card summary from objective / creative_direction section body.
 * Mirrors BriefSectionSeedService::summaryFromSections on the backend.
 * Returns undefined when neither primary description section exists.
 */
export function summaryFromBriefSections(sections) {
  const list = Array.isArray(sections) ? sections : [];
  const primary = list.find((s) => s && s.typeId === 'objective')
    || list.find((s) => s && s.typeId === 'creative_direction');
  if (!primary) {
    return undefined;
  }
  const plain = htmlToText(primary.content || '');
  const firstLine = plain.split(/\r\n|\r|\n/).map((line) => line.trim()).find(Boolean) || '';
  return firstLine.slice(0, 500);
}
